// Import the functions you need from the SDKs you need
import { initializeApp, getApps } from "firebase/app";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage"; // Import getStorage
import { getAuth, GoogleAuthProvider } from "firebase/auth";
import { getDatabase } from "firebase/database";
import { getFunctions, httpsCallable } from "firebase/functions";
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check";

// Your web app's Firebase configuration
const firebaseConfigDev = {
  apiKey: "AIzaSyCKbLuYpAnG-yQrYiHkKZHkP0boWq0dnBU",
  authDomain: "dev.datasculpture.ai",
  databaseURL: "https://data-sculpture-ai-default-rtdb.firebaseio.com",
  projectId: "data-sculpture-ai",
  storageBucket: "data-sculpture-ai.appspot.com",
  messagingSenderId: "15991981569",
  appId: "1:15991981569:web:6c4eb4714cf9fdbbf76200",
  measurementId: "G-801W1SBFSV",
};

const firebaseConfigProd = {
  apiKey: "AIzaSyAgwDmGy_uKhkPeyeGW0W7XPbxEpGuVD-I",
  authDomain: "app.datasculpture.ai",
  databaseURL: "https://data-sculpture-default-rtdb.firebaseio.com",
  projectId: "data-sculpture",
  storageBucket: "data-sculpture.firebasestorage.app",
  messagingSenderId: "882954955778",
  appId: "1:882954955778:web:6c169b02a9af5705a90b1b",
  measurementId: "G-5PT3F0BHLJ",
};

// Initialize Firebase
const hostname = window.location.hostname;
const prodHostnames = ["data-sculpture.web.app", "data-sculpture.firebaseapp.com", "app.datasculpture.ai"];
const isVercelPreview = hostname.includes(".vercel.app");
const isFirebasePreview = hostname.includes("--") && (hostname.includes(".web.app") || hostname.includes(".firebaseapp.com"));
const isPreviewEnv = isVercelPreview || isFirebasePreview;
// For preview deployments (Vercel and Firebase), use dev config to avoid Firebase auth domain restrictions
// Vercel generates URLs like "ds-front-git-branch-team.vercel.app"
// Firebase generates URLs like "projectname--pr-number-hash.web.app"
// These dynamic URLs can't be pre-configured in Firebase authorized domains
const isProduction = import.meta.env.MODE === "production" && prodHostnames.includes(hostname) && !isVercelPreview && !isFirebasePreview;
// const isDevelopment = import.meta.env.MODE === "production" && devHostnames.includes(hostname) && !isVercelPreview && !isFirebasePreview;
// Local & emulator detection
const isLocalhost = ["localhost", "127.0.0.1"].includes(hostname) || hostname.endsWith(".local");
// Detect Vitest / test environment to avoid initializing network-dependent services
const isTestEnv = !!import.meta?.env?.VITEST;
// Allow opt-out by setting VITE_USE_FIREBASE_EMULATORS="false" (test env always forces true)
const useEmulators = (isLocalhost && import.meta?.env?.VITE_USE_FIREBASE_EMULATORS !== "false") || isTestEnv;

// Determine Firebase project config to use
let firebaseConfig;
if (isProduction) {
  firebaseConfig = firebaseConfigProd;
} else {
  firebaseConfig = firebaseConfigDev;
  console.info("[firebase] Using dev Firebase project config locally");
}

let app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];

const db = getFirestore(app);
const storage = getStorage(app);
const auth = getAuth(app);
const realtime = getDatabase(app);
// Using default region; adjust if functions deployed to a specific region (e.g., getFunctions(app, "us-central1"))
const functions = getFunctions(app);
const googleProvider = new GoogleAuthProvider();

// Initialize App Check (site key fetched from callable to avoid hardcoding in bundle)
let appCheck; // exported after initialization
const appCheckReadyPromise = async () => {
  try {
    const getAppCheckSiteKey = httpsCallable(functions, "getAppCheckSiteKey");
    const { data } = await getAppCheckSiteKey();
    const siteKey = data?.siteKey;
    if (!siteKey) {
      console.warn("[firebase] AppCheck: no siteKey returned; skipping AppCheck initialization");
      return null;
    }
    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(siteKey),
      isTokenAutoRefreshEnabled: true,
    });
    return { siteKey };
  } catch (e) {
    console.warn("[firebase] AppCheck initialization skipped:", e?.message || e);
    return null;
  }
};

// Only initialize App Check on hosted environments (skip during local emulator usage to avoid remote call & captcha requirements)
// Only run App Check on production hosts to avoid ReCAPTCHA domain restrictions on dev/staging
if (!useEmulators && !isTestEnv && !isPreviewEnv && isProduction) {
  try {
    appCheckReadyPromise();
  } catch (e) {
    console.error("AppCheck initialization error:", e);
  }
} else {
  console.info("[firebase] AppCheck disabled for this environment (emulator/test/preview/non-prod)");
}

// Connect to local emulators when running on localhost (unless explicitly disabled)
// Dynamically connect to local emulators to avoid hard dependencies during tests/mocks
const connectLocalEmulators = async () => {
  // Use the same hostname as the app (usually 'localhost') to avoid an extra cross-origin (127.0.0.1 vs localhost) preflight.
  const EMULATOR_HOST = "127.0.0.1"; // prefer 'localhost' so origin matches dev server
  try {
    const fsMod = await import("firebase/firestore");
    try {
      // Some Vitest mocks throw when accessing missing properties; wrap in try/catch
      if (typeof fsMod.connectFirestoreEmulator === "function") {
        fsMod.connectFirestoreEmulator(db, EMULATOR_HOST, 8080);
      }
    } catch (_) {
      console.info("[firebase] Skipping Firestore emulator connect (mocked env)");
    }
  } catch (e) {
    console.info("[firebase] Firestore emulator module not available:", e?.message || e);
  }

  try {
    const fnMod = await import("firebase/functions");
    try {
      if (typeof fnMod.connectFunctionsEmulator === "function") {
        fnMod.connectFunctionsEmulator(functions, EMULATOR_HOST, 5001);
      }
    } catch (_) {
      console.info("[firebase] Skipping Functions emulator connect (mocked env)");
    }
  } catch (e) {
    console.info("[firebase] Functions emulator module not available:", e?.message || e);
  }

  try {
    const stMod = await import("firebase/storage");
    try {
      if (typeof stMod.connectStorageEmulator === "function") {
        stMod.connectStorageEmulator(storage, EMULATOR_HOST, 9199);
      }
    } catch (_) {
      console.info("[firebase] Skipping Storage emulator connect (mocked env)");
    }
  } catch (e) {
    console.info("[firebase] Storage emulator module not available:", e?.message || e);
  }

  try {
    const dbMod = await import("firebase/database");
    try {
      if (typeof dbMod.connectDatabaseEmulator === "function") {
        dbMod.connectDatabaseEmulator(realtime, EMULATOR_HOST, 9000);
      }
    } catch (_) {
      console.info("[firebase] Skipping RealtimeDB emulator connect (mocked env)");
    }
  } catch (e) {
    console.info("[firebase] RealtimeDB emulator module not available:", e?.message || e);
  }

  console.info(`[firebase] Connected to local emulators at ${EMULATOR_HOST} (Firestore:8080 Functions:5001 Storage:9199 RealtimeDB:9000)`);
  console.info(`[firebase] Active projectId (client): ${firebaseConfig.projectId}`);
  if (firebaseConfig.projectId !== "data-sculpture" && firebaseConfig.projectId !== "data-sculpture-ai") {
    console.warn("[firebase] Unexpected projectId detected:", firebaseConfig.projectId);
  }
};

if (useEmulators) {
  try {
    // To reduce noisy duplicate connections in hot-reload, guard with a global flag
    if (!window.__FIREBASE_EMULATORS_CONNECTED__) {
      // In Vitest/test environments, skip emulator connection entirely to avoid mock export traps
      if (!isTestEnv) {
        // Fire and forget
        void connectLocalEmulators();
      } else {
        console.info("[firebase] Test env detected: skipping emulator connections");
      }
      window.__FIREBASE_EMULATORS_CONNECTED__ = true;
    }
  } catch (e) {
    console.warn("[firebase] Failed to connect to emulators", e);
  }
}

export {
  db,
  realtime,
  storage,
  auth,
  functions,
  googleProvider,
  appCheck,
  appCheckReadyPromise,
  isProduction,
  isVercelPreview,
  isFirebasePreview,
  useEmulators,
};
